import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { withPixelLineHeight } from '../../../base/styles/functions.web';
import { admitMultiple } from '../../../lobby/actions.web';
import { getKnockingParticipants, getLobbyEnabled } from '../../../lobby/functions';
import { showOverflowDrawer } from '../../../toolbox/functions.web';
import LobbyParticipantItems from './LobbyParticipantItems';
import { isMobileBrowser } from "../../../base/environment/utils";
import { getVisitorsCount, getVisitorsInQueueCount, isVisitorsLive } from "../../../visitors/functions";
import { isNeedShowElectronOnlyElements } from "../../../base/environment/utils_web";
import { getWindowQueryData } from "../../../desktop-picker/functions";
const useStyles = makeStyles()(theme => {
    return {
        separateLineContainer: {
            padding: '0 2px',
            marginBottom: '20px',
        },
        marginTop: {
            marginTop: '16px',
        },
        drawerActions: {
            listStyleType: 'none',
            margin: 0,
            padding: 0
        },
        drawerItem: {
            alignItems: 'center',
            color: theme.palette.text01,
            display: 'flex',
            padding: '12px 16px',
            ...withPixelLineHeight(theme.typography.bodyShortRegularLarge),
            '&:first-child': {
                marginTop: '15px'
            },
            '&:hover': {
                cursor: 'pointer',
                background: theme.palette.action02
            }
        },
        icon: {
            marginRight: 16
        },
        headingContainer: {
            padding: '0 0 0 4px',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '10px',
            '&.is-mobile': {
                padding: '8px 16px 10px 16px',
                marginBottom: 0,
            }
        },
        heading: {
            fontFamily: 'Lato Regular',
            fontWeight: 'normal',
            textTransform: 'uppercase',
            fontSize: '12px',
            lineHeight: '15px',
            color: 'rgba(255, 255, 255, 0.4)',
            '&.is-mobile': {
                fontFamily: 'Lato Regular',
                fontWeight: 'normal',
                textTransform: 'uppercase',
                fontSize: '13px',
                lineHeight: '18px',
                letterSpacing: '-0.1px',
                color: 'rgba(255, 255, 255, 0.3)'
            }
        },
        link: {
            fontFamily: 'Lato Regular',
            fontWeight: 'normal',
            fontSize: '12px',
            lineHeight: '15px',
            color: 'rgba(0, 107, 224, 1)',
            cursor: 'pointer',
            '&:hover': {
                color: 'rgba(0, 88, 184, 1)',
            }
        }
    };
});
/**
 * Component used to display a list of participants waiting in the lobby.
 *
 * @returns {ReactNode}
 */
export default function LobbyParticipants() {
    const lobbyEnabled = useSelector(getLobbyEnabled);
    const participants = useSelector(getKnockingParticipants);
    const { t } = useTranslation();
    const isMobile = isMobileBrowser();
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const admitAll = useCallback(() => {
        dispatch(admitMultiple(participants));
    }, [dispatch, participants]);
    const overflowDrawer = useSelector(showOverflowDrawer);
    const visitorsCount = useSelector(getVisitorsCount);
    const visitorsInQueueCount = useSelector(getVisitorsInQueueCount);
    const isLive = useSelector(isVisitorsLive);
    const showVisitorsInQueue = visitorsInQueueCount > 0 && isLive === false;
    const isVisitorListVisible = visitorsCount > 0 && showVisitorsInQueue;
    const { isWebVersion } = getWindowQueryData();
    if (!lobbyEnabled || !participants.length) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        (isNeedShowElectronOnlyElements() || isVisitorListVisible || isWebVersion) ? (React.createElement("div", { className: classes.separateLineContainer },
            React.createElement("div", { className: cx('dotted-separate-line') }))) : (React.createElement("div", { className: classes.marginTop })),
        React.createElement("div", { className: cx(classes.headingContainer, isMobile && 'is-mobile') },
            React.createElement("div", { className: cx(classes.heading, isMobile && 'is-mobile') }, isMobile ? t('participantsPane.headings.lobbyMobile') : t('participantsPane.headings.lobby', { count: participants.length })),
            participants.length > 1
                && React.createElement("div", { className: classes.link, onClick: admitAll }, t('participantsPane.actions.admitAll'))),
        React.createElement(LobbyParticipantItems, { openDrawerForParticipant: () => null, overflowDrawer: overflowDrawer, participants: participants })));
}
